import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { useGameIdFromRoute, usePublisherIdFromRoute } from "./WithGameIdFromRoute";
import {
  useGame,
  useGamePublishers,
  useGameSlugs,
  useGameTopics,
  useGeneratePnpLink,
  useMyEntitlements,
  useMyProfile,
  usePublisherGameAsDesigner,
  usePublisherGameStatistics,
  useUpdatePublisherGamev2,
} from "@seabrookstudios/pitch2table";
import { PublisherInterestStatusUpdate } from "./publisher-interest/PublisherInterestStatusUpdate";
import { PublisherInterestProfileCard } from "./publisher-interest/PublisherInterestProfileCard";
import { PublisherInterestActivityCard } from "./publisher-interest/PublisherInterestActivityCard";
import { PublisherInterestRulesSentSelect } from "./publisher-interest/PublisherInterestRulesSentSelect";
import { PublisherInterestSellsheetSentSelect } from "./publisher-interest/PublisherInterestSellsheetSentSelect";
import { EditableSwitch } from "../core/EditableSwitch";
import { ExternalInventorRelationsLinks } from "./publisher-interest/ExternalInventorRelationsLinks";
import { Path } from "../Path";
import { useDocumentTitle } from "usehooks-ts";
import { PopUpTopic } from "../message-board/AddTopic";
import { Buttons } from "../core/EditableListItemUrl";
import { shortUrlPnp } from "./model";
import Alert from "@mui/material/Alert";
import { AlwaysOnAppLink } from "../styleguide/AppLink";
import { SessionsForGamePublisher } from "../sessions/SessionsForGamePublisher";
import Confetti from "react-confetti";
import { IsMember, LockedListItem } from "../core/HasEntitlements";
import { getStatAmount } from "@seabrookstudios/pitch2table-core";
import { makeGamePnpViewedById } from "@seabrookstudios/pitch2table-core";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { PublisherFitAnalysisCard } from "./PublisherFitAnalysisCard";
import { TtgdaContractNegotiationInfoBox } from "../contracts/TtgdaRubricCard";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
const ViewPitchDeck = ({ gameId }) => {
  const { data: topics = [] } = useGameTopics(gameId);
  const [topicId, setTopicId] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").TopicId|null} */ (null)
  );

  useEffect(() => {
    const pitchDeck = topics.find((topic) => topic.type === "PitchDeck");

    setTopicId(pitchDeck ? pitchDeck.id : null);
  }, [topics]);

  // Don't render PopUpTopic if topicId is null
  if (!topicId) {
    return null;
  }

  return <PopUpTopic topicId={topicId} tooltip="View pitch deck" />;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.publisherGame
 */
const CustomPnpLink = ({ publisherGame }) => {
  const { data: entitlements } = useMyEntitlements();
  const { data: profile } = useMyProfile();
  const { data: game } = useGame(publisherGame.gameId);
  const { data: slugs = [] } = useGameSlugs(publisherGame.gameId);
  const generatePnp = useGeneratePnpLink(publisherGame.id);

  if (!entitlements || !profile || !game) {
    return <Skeleton />;
  }

  if (!entitlements.isMember) {
    return null;
  }

  const hasSlug = profile.slug && profile.slug.length > 0;

  if (!hasSlug) {
    return (
      <Alert severity="info">
        Setting a username will let you share custom links with the publisher. You can set your username in{" "}
        <AlwaysOnAppLink to={Path.settings().category("about-you")}>your account settings</AlwaysOnAppLink>.
      </Alert>
    );
  }

  if (game.visibility === "Private") {
    return (
      <Alert severity="info">
        {game.name} is set to private. You cannot share a link with publishers. You can change this in the{" "}
        <AlwaysOnAppLink to={Path.game(publisherGame.gameId).slugs()}>game visibility settings</AlwaysOnAppLink>.
      </Alert>
    );
  }

  if (slugs.length === 0) {
    return (
      <Alert severity="info">
        Your game needs a short link. You can set one in the{" "}
        <AlwaysOnAppLink to={Path.game(publisherGame.gameId).slugs()}>game visibility settings</AlwaysOnAppLink>.
      </Alert>
    );
  }

  const onGenerateHandler = async () => {
    await generatePnp.mutateAsync();
  };

  return !publisherGame.pnp || publisherGame.pnp.length === 0 ? (
    <Button variant="outlined" onClick={onGenerateHandler}>
      {generatePnp.isPending ? <CircularProgress size={20} /> : "Generate PNP Link"}
    </Button>
  ) : (
    <Buttons value={shortUrlPnp(profile.slug, slugs[0], publisherGame.pnp)} />
  );
};

/**
 * Component for displaying total game views
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {string} props.pnp
 */
const PublisherViewsCard = ({ gameId, pnp }) => {
  const { data: entitlements } = useMyEntitlements();
  const { data: stats = [] } = usePublisherGameStatistics(gameId, pnp);

  const totalViews = getStatAmount(stats, makeGamePnpViewedById(gameId, pnp), 0);

  if (!entitlements || !entitlements.isMember) {
    return <LockedListItem title="Total Publisher Views" />;
  }

  return (
    <ListItem>
      <ListItemText
        primary="Publisher Views"
        secondary={`Share this custom link with the publisher to track unique views.\rViews: ${totalViews}`}
      />
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.publisherGame
 */
const PublisherSummary = ({ publisherGame }) => {
  const updatePublisherGame = useUpdatePublisherGamev2(publisherGame.id);
  const [showConfetti, setShowConfetti] = useState(false);
  const [pieces, setPieces] = useState(200);
  const [previousStatus, setPreviousStatus] = useState(publisherGame.status);

  useEffect(() => {
    if (previousStatus !== "Signed" && publisherGame.status === "Signed") {
      setShowConfetti(true);
      setTimeout(() => setPieces(0), 7000);
    }
    setPreviousStatus(publisherGame.status);
  }, [publisherGame.status, previousStatus]);

  /**
   * @param {import("@seabrookstudios/pitch2table-core").AttachmentId} rules
   */
  const updateRulesSent = async (rules) => {
    await updatePublisherGame.mutateAsync({
      rules,
      status: undefined,
      sellsheet: undefined,
      sentPNP: undefined,
      sizzle: undefined,
      signedDate: undefined,
      externalPortalUrl: undefined,
      nextActionType: undefined,
      expectedReleaseDate: undefined,
      cleanUpOtherPublisherInterest: undefined,
      publisherProvidedFeedback: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").AttachmentId} sellsheet
   */
  const updateSellsheetSent = async (sellsheet) => {
    await updatePublisherGame.mutateAsync({
      sellsheet,
      status: undefined,
      rules: undefined,
      sentPNP: undefined,
      sizzle: undefined,
      signedDate: undefined,
      externalPortalUrl: undefined,
      nextActionType: undefined,
      expectedReleaseDate: undefined,
      cleanUpOtherPublisherInterest: undefined,
      publisherProvidedFeedback: undefined,
    });
  };

  /**
   * @param {boolean} sentPNP
   */
  const togglePnpFiles = async (sentPNP) => {
    await updatePublisherGame.mutateAsync({
      sentPNP,
      status: undefined,
      rules: undefined,
      sellsheet: undefined,
      sizzle: undefined,
      signedDate: undefined,
      externalPortalUrl: undefined,
      nextActionType: undefined,
      expectedReleaseDate: undefined,
      cleanUpOtherPublisherInterest: undefined,
      publisherProvidedFeedback: undefined,
    });
  };

  return (
    <>
      <Confetti run={showConfetti} numberOfPieces={pieces} />
      <Card elevation={1} square={false}>
        <List>
          <Box p={1}>
            <Box display="flex" flexDirection="row" justifyContent="flex-end">
              <ViewPitchDeck gameId={publisherGame.gameId} />
            </Box>
            <PublisherInterestStatusUpdate publisherGame={publisherGame} />
            <TtgdaContractNegotiationInfoBox status={publisherGame.status} />
            {publisherGame.status !== "ShortListed" && (
              <PublisherInterestRulesSentSelect
                gameId={publisherGame.gameId}
                value={publisherGame.rules}
                onChange={updateRulesSent}
              />
            )}
            {publisherGame.status !== "ShortListed" && (
              <PublisherInterestSellsheetSentSelect
                gameId={publisherGame.gameId}
                value={publisherGame.sellsheet}
                onChange={updateSellsheetSent}
              />
            )}
            {publisherGame.status !== "ShortListed" && (
              <>
                <EditableSwitch
                  initialValue={publisherGame.sentPNP}
                  offLabel="Publisher does not have PNP files"
                  onLabel="Publisher has been sent PNP files"
                  title="PNP Files"
                  onUpdate={togglePnpFiles}
                />
                <IsMember LockedView={<LockedListItem title="Publisher Views" />}>
                  <Stack direction="row" justifyContent="space-between">
                    <Box flex={1}>
                      <PublisherViewsCard gameId={publisherGame.gameId} pnp={publisherGame.pnp} />
                    </Box>
                    <Box display="flex" flex={1} justifyContent="flex-end" alignItems="center">
                      <CustomPnpLink publisherGame={publisherGame} />
                    </Box>
                  </Stack>
                </IsMember>
              </>
            )}
            <ExternalInventorRelationsLinks publisherGame={publisherGame} />
          </Box>
        </List>
      </Card>
    </>
  );
};

export const PublisherInterestInGame = () => {
  const gameId = useGameIdFromRoute();
  const publisherId = usePublisherIdFromRoute();

  const { data: profile } = useMyProfile();
  const { data: publishers = [] } = useGamePublishers(gameId);
  const [publisherGameId, setPublisherGameId] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherGameId|null} */ (null)
  );
  const { data: publisherGame, isLoading: isPublisherGameLoading } = usePublisherGameAsDesigner(publisherGameId);

  useEffect(() => {
    if (!publishers) {
      return;
    }

    const publisherGame = publishers.find((p) => p.publisherId === publisherId && p.gameId === gameId);
    if (!publisherGame) {
      return;
    }

    setPublisherGameId(publisherGame.id);
  }, [publishers, publisherId, gameId]);

  useDocumentTitle(publisherGame ? publisherGame.publisherName : "Pitch2Table");

  if (!gameId || !publisherId) {
    return <EmptyDetailCard text="Select a publisher from the list on the left." />;
  }

  if (isPublisherGameLoading || !publisherGameId) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (!publisherGame || !profile) {
    return <EmptyDetailCard text="There was an issue fetching data." />;
  }

  return (
    <>
      <PublisherInterestProfileCard publisherGame={publisherGame} />
      <Box pt={1} />
      <PublisherFitAnalysisCard publisherGame={publisherGame} startOpen={publisherGame.status === "ShortListed"} />
      <Box pt={1} />
      <PublisherSummary publisherGame={publisherGame} />
      <Box pt={1} />
      <SessionsForGamePublisher gameId={gameId} publisherId={publisherId} />
      <Box pt={1} />
      <PublisherInterestActivityCard publisherGame={publisherGame} />
    </>
  );
};
